import { ChevronRightIcon } from 'components/icons';
import { FIELDS_CLASS, InSectionEditFormContext } from 'components/section-edit/sectionEditFields';
import type { SectionProps } from 'components/section-menu/Section';
import { SectionTitleProvider, useSectionActions } from 'components/section-menu/SectionActionsContext';
import { Badge } from 'components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFormState } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { CreateDialogForm, useCreateDialogRequest } from './CreateDialogContext';
import { CreateDialogFooterErrors } from './CreateDialogFooter';
import {
    CreateDialogRequiredProvider,
    HOME_ATTRIBUTE,
    noteUserActedOn,
    REQUIRED_SECTION,
    RequiredField,
    useRequiredFields,
} from './CreateDialogRequired';

/** On a section's accordion: the key it opens by, which is its title, or the Required section's. */
const SECTION_ATTRIBUTE = 'data-create-dialog-section';

/** On a section, and on a wrapper inside one, that has nothing left to draw: all it held is in Required. */
const EMPTIED_ATTRIBUTE = 'data-create-dialog-emptied';

/** On a sub-section's heading, which is not content of its own. */
const HEADING_ATTRIBUTE = 'data-create-dialog-heading';

/**
 * A section's fields in the create dialog: the two-column grid a section on a record page edits in, on the
 * section's own width, and one column on the full-screen sheet. A field sits in its home and its host, neither of
 * which has a box, so the grid's rules for its children are repeated for what those two hold. A label is one 16px
 * line, and a row of two fields a form lays out itself takes the grid's own gaps.
 */
export const CREATE_DIALOG_FIELDS_CLASS = cn(
    FIELDS_CLASS,
    'max-sm:grid-cols-1!',
    '[&_[data-slot=field-label]]:leading-[1.3333] [&_[data-slot=form-row]]:gap-x-5 [&_[data-slot=form-row]]:gap-y-3',
    '[&>[data-create-dialog-home]>*>*]:m-0! [&>[data-create-dialog-home]>*>*:not(.form-field)]:col-span-full',
    '[&>[data-create-dialog-home]>*>.form-field:has(textarea)]:col-span-full',
    // The grid's muted labels would hold over an invalid field's own red one.
    '[&_[data-invalid]_[data-slot=field-label]]:text-danger-ink'
);

/** What a sub-section's heading carries in the create dialog: it goes when the fields under it have all gone. */
export const CREATE_DIALOG_HEADING_PROPS = {
    [HEADING_ATTRIBUTE]: '',
    className: 'has-[+[data-create-dialog-emptied]]:hidden',
};

/**
 * The Required section's list, whose items are what the hosts moved into it hold. One list on the same grid: a
 * field is one cell, a textarea and a block that is not a field take the row.
 *
 * Every cell spans a label row and a control row, and a field lays its label and its control on those two, so
 * the controls of a row stay level when one label wraps and when one field shows an error under it. The 12px
 * between rows is a margin on each item rather than the grid's gap, which would open inside every field too. The
 * list's own margin takes the first row's back, and the 4px a section keeps above its fields: the first label
 * sits right under the header.
 *
 * What arrived a moment ago has its control tinted: a field that turned required, and one the form mounted
 * required once the user had acted on it.
 */
const REQUIRED_LIST_CLASS = cn(
    CREATE_DIALOG_FIELDS_CLASS,
    '-mt-4 gap-y-0 [&>*>*]:mx-0! [&>*>*]:mt-3! [&>*>*]:mb-0!',
    '[&>*>*:not(.form-field)]:col-span-full [&>*>.form-field:has(textarea)]:col-span-full',
    '[&>*>.form-field]:row-span-2 [&>*>[data-slot=field]]:grid [&>*>[data-slot=field]]:grid-cols-[minmax(0,1fr)]',
    '[&>*>[data-slot=field]]:grid-rows-subgrid [&>*>[data-slot=field]>:last-child]:row-start-2',
    '[&>*>[data-slot=field]>:first-child:not(:last-child)]:self-end',
    '[&>[data-create-dialog-arrived]_:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips],[data-slot=select-trigger])]:bg-changed',
    '[&>[data-create-dialog-arrived]_:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips],[data-slot=select-trigger])]:border-changed-border'
);

function createRequiredList(): HTMLElement {
    const list = document.createElement('div');
    list.className = REQUIRED_LIST_CLASS;
    return list;
}

const CHIP_TONES = {
    warning: 'bg-warning-tint text-warning-ink',
    success: 'bg-success-tint text-success-ink',
    danger: 'bg-danger-tint text-danger-ink',
};

function Chip({ tone, id, count }: { tone: keyof typeof CHIP_TONES; id: string; count?: number }) {
    return (
        <Badge className={cn('font-semibold', CHIP_TONES[tone])}>
            <FormattedMessage id={id} values={{ count }} />
        </Badge>
    );
}

const DRAWN = 'input:not([type="hidden"]), textarea, select, button, img, svg, canvas, video, iframe';

/** Whether anything under `node` is drawn: text, or an element that shows without any. */
function hasContent(node: Node): boolean {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent ?? '').trim() !== '';
    }
    if (!(node instanceof Element) || node.hasAttribute('hidden') || node.hasAttribute(HEADING_ATTRIBUTE)) {
        return false;
    }
    return node.matches(DRAWN) || Array.from(node.childNodes).some(hasContent);
}

/**
 * Marks each wrapper in `content` that held fields and has nothing left to draw now they are in Required, so it
 * takes no room, and says whether `content` itself has anything left.
 */
function markEmptied(content: Element): boolean {
    content.querySelectorAll(`[${EMPTIED_ATTRIBUTE}]`).forEach((element) => element.removeAttribute(EMPTIED_ATTRIBUTE));
    content.querySelectorAll(`[${HOME_ATTRIBUTE}]`).forEach((home) => {
        let wrapper = home.parentElement;
        while (wrapper && wrapper !== content && !hasContent(wrapper)) {
            wrapper.setAttribute(EMPTIED_ATTRIBUTE, '');
            wrapper = wrapper.parentElement;
        }
    });
    return hasContent(content);
}

const INVALID = '[data-invalid], [aria-invalid="true"]';

const FOCUSABLE = ['input', 'textarea', 'select', 'button', '[tabindex]']
    .map((control) => `${control}:not([type="hidden"]):not([disabled]):not([tabindex="-1"])`)
    .join(', ');

/** The keys of the sections that hold something invalid, as they are drawn. */
function sectionsWithInvalid(root: Element): string[] {
    return Array.from(root.querySelectorAll(INVALID), (invalid) =>
        invalid.closest(`[${SECTION_ATTRIBUTE}]`)?.getAttribute(SECTION_ATTRIBUTE)
    ).filter((key): key is string => typeof key === 'string');
}

/** Focuses the first invalid field in the order on screen, which starts with the Required section. */
function focusFirstInvalid(root: Element) {
    for (const invalid of root.querySelectorAll<HTMLElement>(INVALID)) {
        const control = invalid.matches(FOCUSABLE) ? invalid : invalid.querySelector<HTMLElement>(FOCUSABLE);
        if (control) {
            control.focus();
            return;
        }
    }
}

/** The section a field is drawn in, which is where it counts when it is invalid. */
function sectionOf(field: RequiredField): string {
    return field.inRequired ? REQUIRED_SECTION : field.section;
}

/** Which sections are open, by key, and whether their fields to fix are counted yet. */
type SectionsState = {
    open: ReadonlySet<string>;
    setOpen: (key: string, open: boolean) => void;
    /** False until a Create was attempted: an error the form holds before that is not one to fix yet. */
    countsToFix: boolean;
};

const SectionsContext = createContext<SectionsState | null>(null);

/**
 * The names of the sub-sections of the section `content` belongs to, as their headings read. Read off what is
 * drawn, since a form declares them at any depth, and less one that only repeats its section's title.
 */
function subSectionNames(content: Element, sectionKey: string): string {
    return Array.from(content.querySelectorAll(`[${HEADING_ATTRIBUTE}] [data-section-title]`))
        .filter((heading) => heading.getAttribute('data-section-title') !== sectionKey)
        .map((heading) => heading.textContent)
        .join(' · ');
}

type AccordionProps = {
    /** What the section opens by, and what its invalid fields are counted under. */
    sectionKey: string;
    title: ReactNode;
    titleClassName?: string;
    /**
     * Beside the title, in grey. Left out, the names of the section's sub-sections are written there, which the
     * full-screen sheet has no room for.
     */
    hint?: ReactNode;
    /** At the end of the header while the section counts nothing to fix. */
    chip?: ReactNode;
    children: ReactNode;
};

/**
 * One accordion of the create dialog. It stays mounted while closed, so its fields keep their values and still
 * validate. It is not listed while it has nothing to draw, which a form's section is when every field it declares
 * is in Required and it holds nothing else.
 */
function SectionAccordion({ sectionKey, title, titleClassName = 'text-body1', hint, chip, children }: AccordionProps) {
    const sections = useContext(SectionsContext);
    const withErrors = useRequiredFields().filter((field) => field.hasErrors && sectionOf(field) === sectionKey).length;
    const toFix = (sections?.countsToFix ?? true) ? withErrors : 0;
    const contentRef = useRef<HTMLDivElement>(null);
    const hintRef = useRef<HTMLSpanElement>(null);
    const namesSubSections = hint === undefined;

    // Read off the DOM rather than off the fields: a section also holds what is not a field, and a field's host
    // is moved without a render of this section.
    useLayoutEffect(() => {
        const content = contentRef.current;
        const section = content?.closest(`[${SECTION_ATTRIBUTE}]`);
        if (!content || !section) {
            return undefined;
        }
        const mark = () => {
            section.toggleAttribute(EMPTIED_ATTRIBUTE, !markEmptied(content));
            // React puts nothing in a hint it was not given, so the names written there are safe from it.
            if (namesSubSections && hintRef.current) {
                hintRef.current.textContent = subSectionNames(content, sectionKey);
            }
        };
        mark();
        const observer = new MutationObserver(mark);
        observer.observe(content, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [sectionKey, namesSubSections]);

    return (
        <Collapsible
            open={sections?.open.has(sectionKey)}
            onOpenChange={sections ? (open) => sections.setOpen(sectionKey, open) : undefined}
            className="border-neutral-tint border-t first:border-t-0 data-[create-dialog-emptied]:hidden"
            data-create-dialog-section={sectionKey}
        >
            <CollapsibleTrigger className="group flex h-12 w-full items-center gap-2.5 px-6 text-start max-sm:h-13 max-sm:px-4">
                <ChevronRightIcon className="text-foreground-secondary size-4 shrink-0 group-data-[panel-open]:rotate-90 motion-safe:transition-[rotate] motion-safe:duration-150 motion-safe:ease-out rtl:-scale-x-100 rtl:group-data-[panel-open]:-rotate-90" />
                <span className={cn('flex items-center gap-1 font-semibold whitespace-nowrap', titleClassName)}>
                    {title}
                </span>
                {/* Not shown rather than not drawn: it still pushes the chip to the header's end. */}
                <span
                    ref={hintRef}
                    className={cn(
                        'text-meta text-muted-foreground min-w-0 flex-1 truncate',
                        namesSubSections && 'max-sm:invisible'
                    )}
                >
                    {hint}
                </span>
                {toFix > 0 ? <Chip tone="danger" id="create-dialog.errors.section" count={toFix} /> : chip}
            </CollapsibleTrigger>
            <CollapsibleContent keepMounted>
                <div
                    ref={contentRef}
                    className="@container px-6 pt-1 pb-5 max-sm:px-4 [&_[data-create-dialog-emptied]]:hidden"
                >
                    {children}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

/**
 * A form section as the create dialog draws it: an accordion that starts closed, with its sub-sections named
 * beside its title. It holds the fields that are not required as the form stands; the others are in Required.
 */
export function CreateDialogSection({
    title,
    children,
    isHidden = false,
    noTranslation = false,
    tooltip,
}: SectionProps) {
    if (isHidden) {
        return null;
    }

    return (
        <SectionAccordion
            sectionKey={title}
            title={
                <>
                    {noTranslation ? title : <FormattedMessage id={title} />}
                    {tooltip ?? null}
                </>
            }
        >
            <SectionTitleProvider value={title}>
                <InSectionEditFormContext.Provider value={true}>
                    <div className={CREATE_DIALOG_FIELDS_CLASS}>{children}</div>
                </InSectionEditFormContext.Provider>
            </SectionTitleProvider>
        </SectionAccordion>
    );
}

/** The first accordion: every field that is required as the form stands, in the form's order. */
function RequiredSection({ list }: { list: HTMLElement }) {
    const required = useRequiredFields().filter((field) => field.inRequired && field.required);
    const toFill = required.filter((field) => field.isEmpty).length;
    const setToFill = useCreateDialogRequest()?.setToFill;
    const attachList = useCallback(
        (wrapper: HTMLDivElement | null) => {
            wrapper?.append(list);
        },
        [list]
    );
    // The dialog reads the count from above the sections, where the fields are not known.
    useEffect(() => {
        setToFill?.(toFill);
    }, [setToFill, toFill]);

    let chip: ReactNode = null;
    if (toFill > 0) {
        chip = <Chip tone="warning" id="create-dialog.required.to-fill" count={toFill} />;
    } else if (required.length > 0) {
        chip = <Chip tone="success" id="create-dialog.required.all-filled" />;
    }

    return (
        <SectionAccordion
            sectionKey={REQUIRED_SECTION}
            title={<FormattedMessage id={REQUIRED_SECTION} />}
            titleClassName="text-section"
            hint={<FormattedMessage id="create-dialog.required.count" values={{ count: required.length }} />}
            chip={chip}
        >
            <div ref={attachList} />
        </SectionAccordion>
    );
}

type RefusedCreateProps = {
    form: CreateDialogForm;
    onAttempted: (attempted: boolean) => void;
    onRefused: () => void;
};

/**
 * Hears the form's submits: whether a Create was attempted at all, and each one the form's own validation
 * refused, which is a submit that ended with errors.
 *
 * Once one was attempted, a field in error that the form itself gives a value is checked again, so it is no
 * longer counted as one to fix once it is filled: the form checks again by itself only what the user types, and
 * picking a client fills fields the user never touches.
 */
function RefusedCreate({ form, onAttempted, onRefused }: RefusedCreateProps) {
    const { submitCount, isSubmitSuccessful } = useFormState({ control: form.control });
    const attempted = submitCount > 0;

    useEffect(() => {
        onAttempted(attempted);
    }, [attempted, onAttempted]);

    useEffect(() => {
        if (!attempted) {
            return undefined;
        }
        // The check tells of itself under the field's name too: each value is checked once.
        const checked = new Map<string, unknown>();
        return form.subscribe({
            formState: { values: true },
            callback: ({ name, type }) => {
                // A change the user made has a type, and the form checks that one itself.
                if (!name || type !== undefined || !form.getFieldState(name).invalid) {
                    return;
                }
                const value: unknown = form.getValues(name);
                if (!checked.has(name) || checked.get(name) !== value) {
                    checked.set(name, value);
                    void form.trigger(name);
                }
            },
        });
    }, [attempted, form]);

    useEffect(() => {
        if (submitCount > 0 && !isSubmitSuccessful) {
            onRefused();
        }
    }, [submitCount, isSubmitSuccessful, onRefused]);

    return null;
}

type SectionsProps = { list: HTMLElement; children: ReactNode };

function Sections({ list, children }: SectionsProps) {
    const form = useCreateDialogRequest()?.form;
    const withErrors = useRequiredFields().filter((field) => field.hasErrors).length;
    const rootRef = useRef<HTMLDivElement>(null);
    const [open, setOpenKeys] = useState<ReadonlySet<string>>(() => new Set([REQUIRED_SECTION]));
    const [refusals, setRefusals] = useState(0);
    const [attempted, setAttempted] = useState(false);
    // A body that handed the dialog no form has no Create to hear: its fields to fix count as they come.
    const countsToFix = form ? attempted : true;

    // A form asks for one of its sections by title, as it does on a page.
    const { expandSectionSignal, expandSectionTitle } = useSectionActions();
    const [seenExpandSignal, setSeenExpandSignal] = useState(expandSectionSignal);
    if (expandSectionSignal !== seenExpandSignal) {
        setSeenExpandSignal(expandSectionSignal);
        setOpenKeys(new Set(open).add(expandSectionTitle));
    }

    const setOpen = useCallback<SectionsState['setOpen']>((key, isOpen) => {
        setOpenKeys((current) => {
            const next = new Set(current);
            if (isOpen) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);
    const sections = useMemo(() => ({ open, setOpen, countsToFix }), [open, setOpen, countsToFix]);
    const noteUserAction = useCallback(() => noteUserActedOn(list), [list]);

    const openInvalidSections = useCallback(() => {
        const invalid = rootRef.current ? sectionsWithInvalid(rootRef.current) : [];
        if (invalid.length > 0) {
            setOpenKeys((current) => new Set([...current, ...invalid]));
            setRefusals((count) => count + 1);
        }
    }, []);

    useEffect(() => {
        if (refusals === 0) {
            return undefined;
        }
        // On a timer, so it runs after react-hook-form's own deferred focus, which goes by the order the
        // fields registered in rather than the order they are drawn in.
        const timer = setTimeout(() => {
            if (rootRef.current) {
                focusFirstInvalid(rootRef.current);
            }
        });
        return () => clearTimeout(timer);
    }, [refusals]);

    return (
        <SectionsContext.Provider value={sections}>
            {/* The capture handlers hear the user before a field does, also from a list a field draws elsewhere. */}
            <div
                ref={rootRef}
                className="flex flex-col"
                data-create-dialog-sections=""
                onPointerDownCapture={noteUserAction}
                onKeyDownCapture={noteUserAction}
            >
                <RequiredSection list={list} />
                {children}
            </div>
            {form ? <RefusedCreate form={form} onAttempted={setAttempted} onRefused={openInvalidSections} /> : null}
            <CreateDialogFooterErrors count={countsToFix ? withErrors : 0} />
        </SectionsContext.Provider>
    );
}

/**
 * The create dialog's sections: Required first and open, then the form's own, closed. Any number can be open.
 *
 * A Create the form's validation refused opens every section that holds an invalid field and focuses the first
 * of them on screen. Once a Create was attempted, each section counts its fields to fix in its header, and the
 * dialog's footer their total; the counts follow the fields as they are fixed.
 */
export function CreateDialogSections({ children }: { children: ReactNode }) {
    const [list] = useState(createRequiredList);

    return (
        <CreateDialogRequiredProvider list={list}>
            <Sections list={list}>{children}</Sections>
        </CreateDialogRequiredProvider>
    );
}
